import { useEffect, useRef } from 'react';
import { Button, Dropdown, message, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import Vditor from 'vditor';
import 'vditor/dist/index.css';
import Clipboard from 'clipboard';
import { saveAs } from 'file-saver';
import md from 'markdown-it';
import texmath from 'markdown-it-texmath';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import styles from './index.module.scss';

// markdown-it with KaTeX for export (synchronous, self-contained output)
const ExportMd = md({ breaks: true }).use(texmath, {
  engine: katex,
  delimiters: 'dollars',
  katexOptions: { throwOnError: false, output: 'html' },
});

// Fix math spacing: `$ formula $` → `$formula$` (same as OcrOutput)
const fixMarkdownMathSpacing = (text) => {
  if (!text) return '';
  const regex = /(\\\$)|(`{1,3}[\s\S]*?`{1,3})|(\${1,2})\s+([\s\S]+?)\s+(\${1,2})/g;
  return text.replace(regex, (match, escaped, codeBlock, dollarStart, content, dollarEnd) => {
    if (escaped || codeBlock) return match;
    if (dollarStart && dollarStart === dollarEnd) {
      return `${dollarStart}${content.trim()}${dollarEnd}`;
    }
    return match;
  });
};

export const RightPanel = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const inputValue = useSelector((state) => state.global.inputValue);
  const theme = useSelector((state) => state.global.theme);

  const vditorRef = useRef(null);
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);
  const isReady = useRef(false);

  // Initialize Vditor
  useEffect(() => {
    const initialValue =
      inputValue || localStorage.getItem('historyValue') || '';

    editorRef.current = new Vditor(vditorRef.current, {
      mode: 'ir',
      value: initialValue,
      theme: theme === 'dark' ? 'dark' : 'classic',
      icon: 'ant',
      toolbar: [],
      toolbarConfig: { hide: true },
      cache: { enable: false },
      math: {
        engine: 'MathJax',
        inlineDigit: true,
      },
      placeholder: t('rightPanel.editorPlaceholder'),
      input: (value) => {
        isInternalChange.current = true;
        dispatch({ type: 'global/setInputValue', payload: value });
        localStorage.setItem('historyValue', value);
      },
      after: () => {
        isReady.current = true;
        editorRef.current?.setTheme(
          theme === 'dark' ? 'dark' : 'classic',
          theme === 'dark' ? 'dark' : 'light',
          theme === 'dark' ? 'dracula' : 'github'
        );
      },
    });

    return () => {
      editorRef.current?.destroy();
      editorRef.current = null;
      isReady.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external inputValue changes (e.g. OCR results) to Vditor
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (isReady.current && editorRef.current) {
      editorRef.current.setValue(inputValue || '');
    }
  }, [inputValue]);

  // Sync theme changes
  useEffect(() => {
    if (isReady.current && editorRef.current) {
      editorRef.current.setTheme(
        theme === 'dark' ? 'dark' : 'classic',
        theme === 'dark' ? 'dark' : 'light',
        theme === 'dark' ? 'dracula' : 'github'
      );
    }
  }, [theme]);

  useEffect(() => {
    const clipboard = new Clipboard('#copyButton');
    clipboard.on('success', () => {
      message.success(t('rightPanel.copySuccess'));
    });
    return () => {
      clipboard.destroy();
    };
  }, [t]);

  const handleExport = async ({ key }) => {
    if (!editorRef.current) return;
    const markdown = editorRef.current.getValue();
    if (!markdown) {
      message.warning(t('rightPanel.noContentToDownload'));
      return;
    }

    try {
      if (key === 'md') {
        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        saveAs(blob, 'ocr-result.md');
        message.success(t('rightPanel.downloadSuccess'));
        return;
      }

      // Fix math spacing and render with KaTeX (synchronous, self-contained)
      const fixed = fixMarkdownMathSpacing(markdown);
      const renderedHtml = ExportMd.render(fixed);

      if (key === 'docx') {
        const html2canvas = (await import('html2canvas')).default;
        const container = document.createElement('div');
        container.innerHTML = renderedHtml;
        container.style.cssText = 'position:fixed;left:0;top:0;width:794px;padding:40px;background:#fff;color:#000;font-family:serif;font-size:14px;line-height:1.8;z-index:99999;';
        document.body.appendChild(container);
        await new Promise((r) => setTimeout(r, 100));

        // Convert each KaTeX math element to a PNG image for Word compatibility
        const mathEls = container.querySelectorAll('.katex');
        for (const el of mathEls) {
          try {
            const canvas = await html2canvas(el, { scale: 2, backgroundColor: null, logging: false });
            const img = document.createElement('img');
            img.src = canvas.toDataURL('image/png');
            img.width = el.offsetWidth;
            img.height = el.offsetHeight;
            img.style.verticalAlign = 'middle';
            el.replaceWith(img);
          } catch { /* keep original if conversion fails */ }
        }

        const docHtml = container.innerHTML;
        document.body.removeChild(container);
        const docContent = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="utf-8">
<style>
@page {
  size: A4;
  margin: 18mm 15mm;
}
body {
  font-family: serif;
  font-size: 14px;
  line-height: 1.8;
  color: #000;
  margin: 0;
  padding: 0;
}
.doc-page {
  width: 180mm;
  max-width: 100%;
  margin: 0 auto;
  overflow-wrap: anywhere;
  word-break: break-word;
}
img {
  display: inline-block;
  max-width: 100% !important;
  width: auto !important;
  height: auto !important;
  vertical-align: middle;
}
table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
pre, code {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
</head>
<body><div class="doc-page">${docHtml}</div></body>
</html>`;
        const blob = new Blob([docContent], { type: 'application/msword' });
        saveAs(blob, 'ocr-result.doc');
      } else if (key === 'pdf') {
        const html2pdf = (await import('html2pdf.js')).default;
        const container = document.createElement('div');
        container.innerHTML = renderedHtml;
        container.style.cssText = 'width:794px;padding:40px;background:#fff;color:#000;font-family:serif;font-size:14px;line-height:1.8;';
        document.body.appendChild(container);
        await html2pdf().set({
          margin: 10,
          filename: 'ocr-result.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        }).from(container).save();
        document.body.removeChild(container);
      }
      message.success(t('rightPanel.downloadSuccess'));
    } catch (error) {
      message.error(t('rightPanel.downloadFailed'));
    }
  };

  const exportMenuItems = [
    { key: 'md', label: t('rightPanel.markdownExport') },
    { key: 'docx', label: t('rightPanel.wordExport') },
    { key: 'pdf', label: 'PDF (.pdf)' },
  ];

  return (
    <div className={styles.rightPanel}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft} />
        <div className={styles.toolbarRight}>
          <Tooltip title={t('rightPanel.copyContent')}>
            <Button
              id="copyButton"
              icon={<CopyOutlined />}
              size="small"
              data-clipboard-text={inputValue}
            >
              {t('rightPanel.copy')}
            </Button>
          </Tooltip>
          <Dropdown menu={{ items: exportMenuItems, onClick: handleExport }}>
            <Button
              icon={<DownloadOutlined />}
              size="small"
              type="primary"
            >
              {t('rightPanel.download')}
            </Button>
          </Dropdown>
        </div>
      </div>

      <div className={styles.contentContainer}>
        <div ref={vditorRef} className={styles.vditorContainer} />
      </div>
    </div>
  );
};
